import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Video, Calendar, Clock, Globe, Building2, ChevronDown, ChevronUp, Sparkles, TrendingUp, MessageSquare, CheckCircle2, AlertCircle, User, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import BackButton from '../components/BackButton';
import './MeetingNotesPage.css';

const MeetingNotesPage = () => {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchMeetings = async () => {
      setLoading(true);
      try {
        // TODO: API entegrasyonu
        await new Promise(r => setTimeout(r, 800));
        setMeetings([]);
      } catch {
        setMeetings([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMeetings();
  }, []);

  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  // Group meetings by date
  const grouped = meetings.reduce((acc, m) => {
    const date = formatDate(m.startTime);
    if (!acc[date]) acc[date] = [];
    acc[date].push(m);
    return acc;
  }, {});

  const filteredDates = Object.keys(grouped);

  return (
    <div className="meetings-page">
      <SEO
        title="Toplantı Notlarım"
        description="Toplantı kayıtlarınızı, yapay zeka özetlerini ve konuşma çevirilerini görüntüleyin."
        path="/panel/toplantilarim"
        noindex
      />
      <div className="meetings__container">
        <BackButton />

        <div className="meetings__header">
          <div className="meetings__header-icon">
            <Video size={22} />
          </div>
          <div>
            <h1 className="meetings__title">Toplantı Notlarım</h1>
            <p className="meetings__subtitle">Toplantı kayıtların, yapay zeka özetleri ve konuşma çevirileri.</p>
          </div>
        </div>

        {/* Filters */}
        <div className="meetings__filters">
          <button className={`meetings__filter ${filter === 'all' ? 'meetings__filter--active' : ''}`} onClick={() => setFilter('all')}>
            Tümü
          </button>
          <button className={`meetings__filter ${filter === 'today' ? 'meetings__filter--active' : ''}`} onClick={() => setFilter('today')}>
            Bugün
          </button>
          <button className={`meetings__filter ${filter === 'week' ? 'meetings__filter--active' : ''}`} onClick={() => setFilter('week')}>
            Bu Hafta
          </button>
          <button className={`meetings__filter ${filter === 'month' ? 'meetings__filter--active' : ''}`} onClick={() => setFilter('month')}>
            Bu Ay
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="meetings__loading">
            <Loader2 size={24} className="meetings__spinner" />
            <p>Toplantılar yükleniyor...</p>
          </div>
        ) : meetings.length === 0 ? (
          <div className="meetings__empty">
            <Video size={44} />
            <h3>Henüz toplantı kaydın yok</h3>
            <p>Toplantılarınız gerçekleştikçe burada listelenecek. HireThink toplantı çevirici ile yapılan görüşmelerin kayıtları, özetleri ve analizleri otomatik olarak oluşturulur.</p>
          </div>
        ) : (
          <div className="meetings__timeline">
            {filteredDates.map(date => (
              <div key={date} className="meetings__day">
                <div className="meetings__day-label">
                  <Calendar size={14} />
                  {date}
                </div>

                <div className="meetings__day-list">
                  {grouped[date].map(meeting => (
                    <div key={meeting.id} className={`meetings__card ${expandedId === meeting.id ? 'meetings__card--expanded' : ''}`}>

                      {/* Card Header, always visible */}
                      <button className="meetings__card-header" onClick={() => toggleExpand(meeting.id)}>
                        <div className="meetings__card-dot" />
                        <div className="meetings__card-info">
                          <h3 className="meetings__card-title">{meeting.title}</h3>
                          <div className="meetings__card-meta">
                            <span><Building2 size={13} /> {meeting.companyName}</span>
                            <span><Clock size={13} /> {formatTime(meeting.startTime)} - {formatTime(meeting.endTime)} · {meeting.duration} dk</span>
                            <span><Globe size={13} /> {meeting.language || 'İngilizce'}</span>
                          </div>
                        </div>
                        <div className="meetings__card-toggle">
                          {expandedId === meeting.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                      </button>

                      {/* Expanded content */}
                      {expandedId === meeting.id && (
                        <div className="meetings__card-body">

                          {/* AI Summary */}
                          <div className="meetings__section meetings__section--summary">
                            <h4 className="meetings__section-title">
                              <Sparkles size={15} /> Yapay Zeka Özeti
                            </h4>
                            <p className="meetings__section-text">{meeting.aiSummary}</p>

                            {meeting.keyTopics && meeting.keyTopics.length > 0 && (
                              <div className="meetings__topics">
                                <span className="meetings__topics-label">Öne çıkan konular:</span>
                                <div className="meetings__topics-tags">
                                  {meeting.keyTopics.map((t, i) => (
                                    <span key={i} className="meetings__topic-tag">{t}</span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {meeting.actionItems && meeting.actionItems.length > 0 && (
                              <div className="meetings__actions">
                                <span className="meetings__actions-label">Sonraki adımlar:</span>
                                <ul className="meetings__actions-list">
                                  {meeting.actionItems.map((a, i) => (
                                    <li key={i}><CheckCircle2 size={13} /> {a}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>

                          {/* AI Analysis */}
                          {meeting.analysis && (
                            <div className="meetings__section meetings__section--analysis">
                              <h4 className="meetings__section-title">
                                <TrendingUp size={15} /> Yapay Zeka Analizi
                              </h4>
                              <div className="meetings__analysis-grid">
                                {meeting.analysis.score != null && (
                                  <div className="meetings__analysis-score">
                                    <div className="meetings__score-circle">
                                      <span className="meetings__score-value">{meeting.analysis.score}</span>
                                      <span className="meetings__score-label">Puan</span>
                                    </div>
                                  </div>
                                )}
                                <div className="meetings__analysis-details">
                                  {meeting.analysis.strengths && meeting.analysis.strengths.length > 0 && (
                                    <div className="meetings__analysis-group">
                                      <span className="meetings__analysis-group-title meetings__analysis-group-title--green">
                                        <CheckCircle2 size={13} /> Güçlü Yönler
                                      </span>
                                      <ul>
                                        {meeting.analysis.strengths.map((s, i) => <li key={i}>{s}</li>)}
                                      </ul>
                                    </div>
                                  )}
                                  {meeting.analysis.improvements && meeting.analysis.improvements.length > 0 && (
                                    <div className="meetings__analysis-group">
                                      <span className="meetings__analysis-group-title meetings__analysis-group-title--orange">
                                        <AlertCircle size={13} /> Gelişim Alanları
                                      </span>
                                      <ul>
                                        {meeting.analysis.improvements.map((s, i) => <li key={i}>{s}</li>)}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Conversation Detail */}
                          {meeting.conversation && meeting.conversation.length > 0 && (
                            <div className="meetings__section meetings__section--conversation">
                              <h4 className="meetings__section-title">
                                <MessageSquare size={15} /> Konuşma Detayı
                              </h4>
                              <div className="meetings__conversation">
                                {meeting.conversation.map((msg, i) => (
                                  <div key={i} className={`meetings__msg meetings__msg--${msg.speaker}`}>
                                    <div className="meetings__msg-avatar">
                                      {msg.speaker === 'them' ? <Building2 size={12} /> : <User size={12} />}
                                    </div>
                                    <div className="meetings__msg-content">
                                      <div className="meetings__msg-time">{msg.time}</div>
                                      <div className="meetings__msg-bubble">
                                        <p className="meetings__msg-original">{msg.original}</p>
                                        <p className="meetings__msg-translation">{msg.translation}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingNotesPage;
